<template>
  <span v-resize="checkMobile" :style="isMobile ? 'padding: 5px !important;' : ''">
    <loading :active.sync="isLoading" :is-full-page="true" color="#1976d2"
      :background-color="$vuetify.theme.dark ? '#000' : ''"></loading>
    <v-card data-testid="clients-page" class="pa-3">
      <!-- FT-143: командная шапка вместо тулбаров со свитчами; фильтры и поиск — в ней -->
      <clients-command-bar :filters="filters" :sources="sources" :branches="branches"
        :branch-text="branchCity" :xlsx-loading="isLoading"
        :show-taxi-sync="!!(company && company.taxi_park_type && company.taxi_park_type != 'not_taxi')"
        :taxi-sync-loading="isLoadingSyncTaxiDrivers"
        @input="filters = $event" @reset="resetFilters" @refresh="getDataFromApi"
        @export-xlsx="toXlsx" @sync-taxi="syncTaxiDrivers" @open-duplicates="duplicatesDialog = true" />
      <!-- FT-143: одна таблица для обоих типов клиентов. 11 заголовков ↔ 11 td -->
      <v-data-table :headers="headers" :items="clients" :loading="isLoading"
        :loading-text="$t('custom.loading_table')" :items-per-page="50" mobile-breakpoint="100"
        :server-items-length.sync="total" :options.sync="tableOptions" :page.sync="page" sort-by="id" sort-desc
        :class="!isMobile ? '' : 'is-mobile'" dense :footer-props="tableFooterProps([50, 100, 200], `{0} ${$t('custom.of')} ${total}`)">
        <template slot="item" slot-scope="props">
          <tr :class="rowClass(props.item)" @dblclick="replaceToClient(props.item)" style="cursor: pointer">
            <td class="text-center">
              <v-icon small :title="props.item.entity ? $t('clients_list.entities') : $t('clients_list.persons')">
                {{ props.item.entity ? "mdi-domain" : "mdi-account" }}
              </v-icon>
            </td>
            <td class="text-center">
              <router-link :to="{ path: `/clients/${props.item.id}` }" class="text-decoration-none">{{ props.item.id
              }}</router-link>
            </td>
            <td class="text-center nowrap">
              <v-icon small class="mr-2" @click="replaceToClient(props.item)" :title="$t('clients.info')">
                mdi-eye-outline
              </v-icon>
              <v-icon small @click="deleteItem(props.item)" v-if="isManagerPlus" :data-testid="`delete-client-${props.item.id}`">
                mdi-delete-forever
              </v-icon>
            </td>
            <td class="text-center">
              <template v-if="props.item.entity">
                {{ props.item.entity_name }}
                <span v-if="props.item.reg_form" class="text-caption grey--text">({{ props.item.reg_form }})</span>
              </template>
              <template v-else>{{ personName(props.item) }}</template>
            </td>
            <td class="text-center">{{ formatPhone(props.item.entity ? props.item.entity_phone : props.item.phone) }}</td>
            <td class="text-center">{{ props.item.email }}</td>
            <td class="text-center">
              <!-- таксопарк: интерактивный чип баланса водителя (диалоги ниже), иначе — число с ⚠️ у минуса -->
              <div v-if="isTaxiPark && props.item.yandex_driver_id" class="d-flex align-center justify-center">
                <v-chip small :color="props.item.balance >= 0 ? 'success' : 'error'"
                  @click="getFullBalance(props.item)">
                  <span v-if="!isLoadingGetFullBalance || !currentDriver || props.item.id != currentDriver.id">{{
                    parseFloat(props.item.balance || 0).toFixed(2) }}</span>
                  <v-icon small v-else>mdi-infinity</v-icon>
                </v-chip>
              </div>
              <template v-else>
                <span v-if="props.item.balance == null"></span>
                <span v-else-if="props.item.balance >= 0">{{ props.item.balance }}</span>
                <span v-else>⚠️&nbsp;{{ props.item.balance }}</span>
              </template>
            </td>
            <td class="text-center">
              {{
                props.item.birthday && props.item.birthday != "Invalid date"
                  ? birthdayFormat(props.item.birthday)
                  : ""
              }}
            </td>
            <td class="text-center">{{ props.item.inn }}</td>
            <td class="text-center">{{ props.item.source }}</td>
            <td class="text-center" v-tooltip="{
              content: branchName(props.item.company_id),
              placement: 'bottom-center',
              classes: ['info'],
              targetClasses: ['it-has-a-tooltip'],
              delay: {
                show: 500,
                hide: 500,
              },
            }">
              {{ branchCity(props.item.company_id) }}
            </td>
          </tr>
        </template>
        <template slot="no-data">
          {{ $t("custom.no_data_in_table") }}
        </template>
        <v-alert slot="no-results" :value="true" color="error" icon="mdi-alert">
          {{ $t("tables.no_search_result", { msg: filters.search }) }}
        </v-alert>
      </v-data-table>
    </v-card>

    <client-duplicates-dialog v-model="duplicatesDialog" :branches="branches" @merged="onDuplicatesMerged" />

    <!-- Модальное окно баланса водителя -->
    <v-dialog v-model="balanceDialog" max-width="600px" persistent>
      <v-card>
        <v-card-text v-if="currentDriver && balanceData">
          <!-- Информация о водителе -->
          <div class="text-center mb-4">
            <h3 class="text-h6" :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">{{ currentDriver.fio }}
            </h3>
            <p class="text-caption" :class="$vuetify.theme.dark ? 'grey--text lighten-1' : 'grey--text darken-1'">
              Yandex.Taxi ID: {{ currentDriver.yandex_driver_id }}
            </p>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- Основной баланс -->
          <v-card flat class="mb-4"
            :class="parseFloat(balanceData.balance || 0) >= 0 ? 'green--lighten-5 green--text--darken-3' : 'red--lighten-5 red--text--darken-3'"
            :color="$vuetify.theme.dark ? (parseFloat(balanceData.balance || 0) >= 0 ? 'green darken-4' : 'red darken-4') : (parseFloat(balanceData.balance || 0) >= 0 ? 'green lighten-5' : 'red lighten-5')">
            <v-card-text class="pa-4">
              <div class="d-flex justify-space-between align-center">
                <span class="text-h6 font-weight-bold" :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">
                  Основной баланс
                </span>
                <v-spacer></v-spacer>
                <span class="text-h5 font-weight-bold"
                  :class="parseFloat(balanceData.balance || 0) >= 0 ? 'green--text' : 'red--text'">
                  {{ parseFloat(balanceData.balance || 0).toFixed(2) }}
                </span>
                <v-btn icon color="success" @click="openTransactionDialog(currentDriver, 'add')"
                  :disabled="isLoadingTransaction" class="mx-1">
                  <v-icon>mdi-plus-circle-outline</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Заблокированный баланс -->
          <v-card flat class="mb-4"
            :class="parseFloat(balanceData.blocked_balance || 0) > 0 ? 'orange--lighten-5 orange--text--darken-3' : 'grey--lighten-5 grey--text--darken-3'"
            :color="$vuetify.theme.dark ? (parseFloat(balanceData.blocked_balance || 0) > 0 ? 'orange darken-4' : 'grey darken-4') : (parseFloat(balanceData.blocked_balance || 0) > 0 ? 'orange lighten-5' : 'grey lighten-5')">
            <v-card-text class="pa-4">
              <div class="d-flex justify-space-between align-center">
                <span class="text-h6 font-weight-bold"
                  :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">Заблокировано</span>
                <span class="text-h5 font-weight-bold"
                  :class="parseFloat(balanceData.blocked_balance || 0) > 0 ? 'orange--text darken-2' : 'grey--text'">
                  {{ parseFloat(balanceData.blocked_balance || 0).toFixed(2) }} ₽
                </span>
              </div>
            </v-card-text>
          </v-card>

          <!-- Детали заблокированного баланса -->
          <v-expand-transition v-if="parseFloat(balanceData.blocked_balance || 0) > 0">
            <v-card flat :color="$vuetify.theme.dark ? 'orange darken-4' : 'orange lighten-5'" class="mb-4">
              <v-card-title :class="$vuetify.theme.dark ? 'white--text' : 'orange--text darken-2'" class="py-2">
                <v-icon left :color="$vuetify.theme.dark ? 'orange lighten-2' : 'orange darken-2'">mdi-lock</v-icon>
                Детали заблокированного баланса
              </v-card-title>
              <v-card-text class="pt-0">
                <v-list dense>
                  <v-list-item v-if="balanceData.details.blocked_tips">
                    <v-list-item-content>
                      <v-list-item-title class="d-flex justify-space-between">
                        <span :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">Чаевые</span>
                        <span class="font-weight-bold"
                          :class="$vuetify.theme.dark ? 'orange--text lighten-2' : 'orange--text darken-2'">
                          {{ parseFloat(balanceData.details.blocked_tips).toFixed(2) }} ₽
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="balanceData.details.blocked_cashless">
                    <v-list-item-content>
                      <v-list-item-title class="d-flex justify-space-between">
                        <span :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">Безналичные</span>
                        <span class="font-weight-bold"
                          :class="$vuetify.theme.dark ? 'orange--text lighten-2' : 'orange--text darken-2'">
                          {{ parseFloat(balanceData.details.blocked_cashless).toFixed(2) }} ₽
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="balanceData.details.blocked_bonuses">
                    <v-list-item-content>
                      <v-list-item-title class="d-flex justify-space-between">
                        <span :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">Бонусы</span>
                        <span class="font-weight-bold"
                          :class="$vuetify.theme.dark ? 'orange--text lighten-2' : 'orange--text darken-2'">
                          {{ parseFloat(balanceData.details.blocked_bonuses).toFixed(2) }} ₽
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="balanceData.details.blocked_financial_statements">
                    <v-list-item-content>
                      <v-list-item-title class="d-flex justify-space-between">
                        <span :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">Финотчеты</span>
                        <span class="font-weight-bold"
                          :class="$vuetify.theme.dark ? 'orange--text lighten-2' : 'orange--text darken-2'">
                          {{ parseFloat(balanceData.details.blocked_financial_statements).toFixed(2) }} ₽
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="balanceData.details.blocked_closing_documents">
                    <v-list-item-content>
                      <v-list-item-title class="d-flex justify-space-between">
                        <span :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">Закрывающие документы</span>
                        <span class="font-weight-bold"
                          :class="$vuetify.theme.dark ? 'orange--text lighten-2' : 'orange--text darken-2'">
                          {{ parseFloat(balanceData.details.blocked_closing_documents).toFixed(2) }} ₽
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-expand-transition>

          <!-- Время обновления -->
          <div class="text-center">
            <v-chip small :color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-2'"
              :text-color="$vuetify.theme.dark ? 'grey lighten-3' : 'grey darken-1'">
              <v-icon left small>mdi-clock-outline</v-icon>
              Обновлено: {{ new Date(balanceData.last_updated).toLocaleString('ru-RU') }}
            </v-chip>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :color="$vuetify.theme.dark ? 'primary' : 'primary'" :text="$vuetify.theme.dark ? false : true"
            :outlined="$vuetify.theme.dark ? true : false" @click="balanceDialog = false">
            {{ $t("custom.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Диалог создания транзакции -->
    <v-dialog v-model="transactionDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline text-center" :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">
          <v-icon left :color="transactionType === 'add' ? 'success' : 'error'">
            {{ transactionType === 'add' ? 'mdi-plus' : 'mdi-minus' }}
          </v-icon>
          {{ transactionType === 'add' ? 'Пополнение баланса' : 'Списание с баланса' }}
          <v-spacer></v-spacer>
          <v-btn small icon color="primary" @click="transactionDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-divider class="mb-4"></v-divider>
          <!-- Информация о водителе -->
          <div class="text-center mb-4" v-if="currentDriver">
            <h3 class="text-h6" :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">
              {{ currentDriver.fio }}
            </h3>
            <p class="text-caption" :class="$vuetify.theme.dark ? 'grey--text lighten-1' : 'grey--text darken-1'">
              Yandex.Taxi ID: {{ currentDriver.yandex_driver_id }}
            </p>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- Форма транзакции -->
          <v-text-field v-model="count.sum" :label="'Сумма (₽)'" type="number" step="0.01" min="0"
            :color="transactionType === 'add' ? 'success' : 'error'"
            :hint="transactionType === 'add' ? 'Сумма для добавления на баланс' : 'Сумма для списания с баланса'"
            persistent-hint required :disabled="isLoadingTransaction">
          </v-text-field>

          <v-textarea v-model="count.description" label="Описание транзакции"
            :color="transactionType === 'add' ? 'success' : 'error'" :hint="'Краткое описание причины транзакции'"
            persistent-hint required :disabled="isLoadingTransaction" rows="1" auto-grow>
          </v-textarea>

          <v-row>
            <v-col cols="12">
              <v-row class="d-flex align-center justify-space-between">
                <v-col cols="12" xs="3" sm="3" md="3" lg="3" xl="3" v-if="company && company.cash_payment">
                  <v-checkbox v-model="count.cash" color="success" :disabled="count.cashless || count.cash_card || count.entity
                    " class="mt-0 mr-0" dense hide-details :style="count.cashless || count.cash_card || count.entity
                      ? 'opacity: 0.4;'
                      : ''
                      ">
                    <template v-slot:label>
                      <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                        content: $t('tooltips.cash_count'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 500,
                          hide: 500,
                        },
                      }">
                        mdi-cash
                      </v-icon>
                    </template>
                  </v-checkbox>
                </v-col>
                <v-col cols="12" xs="3" sm="3" md="3" lg="3" xl="3" v-if="company && company.cashless_payment">
                  <v-checkbox v-model="count.cashless" color="warning" :disabled="count.cash ||
                    count.cash_card ||
                    count.entity
                    " class="mt-0 mr-0" dense hide-details :style="count.cash ||
                      count.cash_card ||
                      count.entity
                      ? 'opacity: 0.4;'
                      : ''
                      ">
                    <template v-slot:label>
                      <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                        content: $t('tooltips.terminal_count'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 500,
                          hide: 500,
                        },
                      }">
                        mdi-contactless-payment-circle-outline
                      </v-icon>
                    </template>
                  </v-checkbox>
                </v-col>
                <v-col cols="12" xs="3" sm="3" md="3" lg="3" xl="3" v-if="company && company.card_to_card_payment">
                  <v-checkbox v-model="count.cash_card" color="info" :disabled="count.cash ||
                    count.cashless ||
                    count.entity
                    " class="mt-0 mr-0" dense hide-details :style="count.cash ||
                      count.cashless ||
                      count.entity
                      ? 'opacity: 0.4;'
                      : ''
                      ">
                    <template v-slot:label>
                      <v-icon class="text-center mr-2" color="info" v-tooltip="{
                        content: $t('money.card_to_card'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 500,
                          hide: 500,
                        },
                      }">
                        mdi-credit-card-check-outline
                      </v-icon>
                    </template>
                  </v-checkbox>
                </v-col>
                <v-col cols="12" xs="3" sm="3" md="3" lg="3" xl="3" v-if="company && company.entity_payment">
                  <v-checkbox v-model="count.entity" color="secondery" class="mt-0 mr-0" dense hide-details :disabled="count.cashless ||
                    count.cash_card ||
                    count.cash
                    " :style="count.cashless ||
                      count.cash_card ||
                      count.cash
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                        content: $t('tooltips.entity_count'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 500,
                          hide: 500,
                        },
                      }">
                    <template v-slot:label>
                      <v-icon class="text-center" color="secondery">
                        mdi-bank
                      </v-icon>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isLoadingTransaction" @click="transactionDialog = false">
            {{ $t("custom.close") }}
          </v-btn>
          <v-btn :color="transactionType === 'add' ? 'success' : 'error'" :loading="isLoadingTransaction"
            :disabled="!count.sum || !count.description" @click="createTransaction">
            {{ transactionType === 'add' ? 'Пополнить' : 'Списать' }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>
<script>
import { clientFullName } from "@/utils/personName";
import { countries } from "./../countries";
import moment from "moment";
import { setMomentLocale } from "@/utils/localeInfra";
import "moment/locale/ru";
import "moment/locale/en-gb";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { saveAs } from "file-saver";
import xlsx from "json-as-xlsx";
import ClientsCommandBar from "@/components/dashboard/clients/ClientsCommandBar.vue";
import ClientDuplicatesDialog from "@/components/dashboard/clients/ClientDuplicatesDialog.vue";
import { isMoneyDeclined } from "@/utils/moneyConfirmGuard";
import { emptyFilters } from "@/components/dashboard/clients/clientsFilterChips";
import { formatPhone } from "@/lib/phone/display";
export default {
  pullToRefresh: true,
  data() {
    return {
      clients: [],
      countries: countries,
      // FT-143: единое состояние фильтров командной шапки (в т.ч. поиск)
      filters: emptyFilters(),
      // distinct-источники компании — опции фильтра, отдаёт clients#list
      sources: [],
      // FT-143: 11 заголовков ↔ 11 td в шаблоне; value сортируемых = allowlist сервера
      headers: [
        { text: "", sortable: false, align: "center", value: "entity" },
        {
          text: this.$t("custom.number"),
          align: "center",
          value: "id",
        },
        { text: this.$t("custom.editor"), sortable: false, align: "center" },
        { text: this.$t("bookings.name"), value: "client_name", align: "center" },
        {
          text: this.$t("custom.phone"),
          sortable: false,
          value: "phone",
          align: "center",
        },
        { text: this.$t("custom.email"), value: "email", align: "center" },
        { text: this.$t("other.balance"), value: "balance", align: "center" },
        {
          text: this.$t("bookings.birthdate"),
          sortable: false,
          value: "birthday",
          align: "center",
        },
        { text: this.$t("custom.tax_number"), value: "inn", align: "center" },
        { text: this.$t("custom.source"), value: "source", align: "center" },
        {
          text: this.$t("custom.branch"),
          value: "company_id",
          align: "center",
        },
      ],
      branches: [],
      tableOptions: {},
      page: 1,
      pageSize: 10,
      per_page: 50,
      sort_by: "id",
      direction: "desc",
      timeout: null,
      pagination: {
        sortBy: ["id"],
        sortDesc: [true],
        page: 1,
        rowsPerPage: 20,
      },
      total: 0,
      // Данные для модального окна баланса
      balanceDialog: false,
      balanceData: null,
      currentDriver: null,
      isMobile: false,
      isLoading: true,
      isLoadingSyncTaxiDrivers: false,
      isLoadingGetFullBalance: false,
      error: "",
      // Данные для диалога транзакций
      transactionDialog: false,
      transactionType: '', // 'add' или 'subtract'
      count: {
        sum: '',
        description: '',
      },
      isLoadingTransaction: false,
      duplicatesDialog: false,
    };
  },
  mounted() {
    let self = this;
  },
  created() {
    let self = this;
    // moment Locale
    setMomentLocale(this.$i18n.locale);
    if (this.$store.getters.isLoggedIn) {
      if (this.user_role == "agent") {
        this.$router.push("/search_free");
      }
      this.isLoading = true;
      this.axios
        .get("/api/v1/main_companies", {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.branches = response.data;
        })
        .catch((error) => {
          this.isLoading = false;
          this.setError(error, this.$t("errors.branch_load"));
          console.log(error);
        });
    } else {
      this.$store.commit("unsetCurrentUser");
    }
  },
  watch: {
    // FT-143: любое изменение фильтров/поиска из командной шапки — с первой страницы.
    // Дебаунс поиска живёт в ClientsCommandBar, сюда прилетает готовое значение.
    filters() {
      this.page = 1;
      this.getDataFromApi();
    },
    tableOptions(pagination) {
      this.page = pagination.page;
      this.per_page = pagination.itemsPerPage;
      this.sort_by = pagination.sortBy[0] ? pagination.sortBy[0] : "id";
      this.direction = pagination.sortDesc[0] ? "desc" : "asc";
      this.getDataFromApi();
    },
  },
  computed: {
    company() {
      return this.$store.getters.getCompany.company;
    },
    isTaxiPark() {
      return !!(this.company &&
        (this.company.taxi_park_type == "iron_park" || this.company.taxi_park_type == "virtual_park"));
    },
    isIos() {
      return Capacitor.getPlatform().toLowerCase() == "ios" ? true : false;
    },
    isNative() {
      return Capacitor.isNativePlatform();
    },
    checkWriteFile() {
      return Capacitor.isNativePlatform() && Filesystem.checkPermissions();
    },
    requestPermissionsWriteFile() {
      return Filesystem.requestPermissions();
    },
  },
  methods: {
    formatPhone,
    // FT-143: только whitelisted-параметры нового clients#list; tri-state шлём
    // строго булевыми, «все» = параметр не отправляется
    filterParams() {
      const f = this.filters;
      const p = {};
      if (f.search) p.search = f.search;
      if (f.entity === true || f.entity === false) p.entity = f.entity;
      if (f.debtor === true || f.debtor === false) p.debtor = f.debtor;
      if (f.category && f.category.length) p.category = f.category;
      if (f.source && f.source.length) p.source = f.source;
      if (f.company_id) p.company_id = f.company_id;
      if (f.birthday_today === true) p.birthday_today = true;
      return p;
    },
    resetFilters() {
      this.filters = emptyFilters();
    },
    onDuplicatesMerged() {
      this.getDataFromApi();
    },
    getDataFromApi() {
      this.isLoading = true;
      this.axios
        .post(
          `/api/v1/clients/list`,
          {
            ...this.filterParams(),
            page: this.page,
            per_page: this.per_page,
            sort_by: this.sort_by,
            direction: this.direction,
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then((response) => {
          this.clients = response.data.clients;
          this.total = response.data.total;
          this.sources = response.data.sources || [];
        })
        .catch((error) => {
          console.log(error);
          this.setError(error, this.$t("errors.clients_load"));
        })
        .finally(() => (this.isLoading = false));
    },
    // Имя физлица в объединённой колонке — из частей (fio сокращённый и бывает пустым)
    personName(client) {
      return clientFullName(client);
    },
    capitalizeFirst(text) {
      if (text) {
        return text[0].toUpperCase() + text.substring(1);
      }
    },
    // FT-143: раскраска категории + подложка должника НЕЗАВИСИМО от категории
    // (раньше должник с пустой/неизвестной категорией не подсвечивался — дыра закрыта
    // осознанно, решение USER «должников выделять»)
    rowClass(client) {
      if (!client) return "";
      const categoryClass = {
        "Новый": "",
        "Постоянный": "green--text",
        "Серый": "grey--text text-decoration-line-through",
        "Чёрный": "black--text text-decoration-line-through",
        "Лояльный": "deep-purple--text",
      }[client.category] || "";
      return `${categoryClass}${client.debtor ? " warning lighten-2" : ""}`.trim();
    },
    deleteItem(client) {
      if (this.isManagerPlus) {
        let self = this;
        this.$confirm({
          title: this.$t("custom.you_sure_title"),
          text: this.$t("custom.you_sure_text"),
          icon: "mdi-alert-circle-outline",
          confirmButtonText: this.$t("custom.you_sure_yes"),
          cancelButtonText: this.$t("custom.you_sure_no"),
        }).then((result) => {
          if (result) {
            this.isLoading = true;
            this.axios
              .delete(`/api/v1/clients/${client.id}`, {
                headers: {
                  Authorization: this.$store.getters.getAuthToken,
                },
              })
              .then(() => {
                this.clients.splice(this.clients.indexOf(client), 1);
                this.$notify(this.$t("custom.deleted"), this.$t("clients.remove"), "success");
              })
              .catch((error) => {
                // check if error is 406
                if (error.response?.status == 406) {
                  this.$notify(this.$t("errors.client_delete"), this.$t("errors.delete_client_has_bookings"), "error", 5000);
                } else {
                  this.setError(error, this.$t("errors.client_delete"))
                }
              })
              .finally(() => (this.isLoading = false));
          }
        });
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    localeClientCategory(category) {
      if (category == "Новый") {
        return this.$t("customer_category.new");
      } else if (category == "Постоянный") {
        return this.$t("customer_category.statement");
      } else if (category == "Серый") {
        return this.$t("customer_category.problem");
      } else if (category == "Чёрный") {
        return this.$t("customer_category.bad");
      } else if (category == "Лояльный") {
        return this.$t("customer_category.loyal");
      } else {
        return "";
      }
    },
    toXlsx() {
      if (this.isStaff) {
        this.isLoading = true;
        this.axios
          .get(`/api/v1/clients`, {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          })
          .then((response) => {
            let clients = [];
            let entity_clients = [];
            response.data.data.forEach((element) => {
              if (!element.attributes.entity) {
                clients.push(element.attributes);
              } else {
                entity_clients.push(element.attributes);
              }
            });
            let data = [
              {
                sheets: "Clients",
                columns: [
                  { label: "ID", value: "id" },
                  { label: this.$t("clients.created_at"), value: "created_at" },
                  { label: this.$t("bookings.name"), value: "name" },
                  {
                    label: this.$t("bookings.middlename"),
                    value: "middlename",
                  },
                  { label: this.$t("bookings.lastname"), value: "lastname" },
                  { label: this.$t("custom.phone"), value: "phone" },
                  { label: "Email", value: "email" },
                  {
                    label: this.$t("bookings.birthdate"),
                    value: (row) => {
                      return row.birthday && row.birthday != "Invalid date"
                        ? this.birthdayFormat(row.birthday)
                        : "";
                    },
                  }, // Custom format
                  {
                    label: this.$t("clients.category"),
                    value: (row) => {
                      return this.localeClientCategory(row.category);
                    },
                  },
                  { label: this.$t("clients.tax_number"), value: "inn" },
                  { label: this.$t("clients.debtor"), value: "debtor" },
                  { label: this.$t("clients.source"), value: "source" },
                  { label: this.$t("clients.country"), value: "country" },
                  { label: this.$t("clients.city"), value: "city" },
                  { label: this.$t("clients.address"), value: "address" },
                  {
                    label: this.$t("clients.branch"),
                    value: (row) => {
                      return this.branchCity(row.company_id);
                    },
                  },
                  {
                    label: this.$t("clients.driver_series"),
                    value: "driver_series",
                  },
                  {
                    label: this.$t("clients.driver_number"),
                    value: "driver_number",
                  },
                  {
                    label: this.$t("clients.driver_issued"),
                    value: "driver_issued",
                  },
                  {
                    label: this.$t("clients.passport_series"),
                    value: "passport_series",
                  },
                  {
                    label: this.$t("clients.passport_number"),
                    value: "passport_number",
                  },
                  {
                    label: this.$t("clients.passport_issued"),
                    value: "passport_issued",
                  },
                  { label: this.$t("clients.sale"), value: "sale" },
                  { label: this.$t("clients.sale_cash"), value: "sale_cash" },
                ],
                content: clients,
              },
              {
                sheets: "Entity Clients",
                columns: [
                  { label: "ID", value: "id" },
                  { label: this.$t("clients.created_at"), value: "created_at" },
                  { label: this.$t("clients.reg_form"), value: "reg_form" },
                  { label: this.$t("companies.name"), value: "entity_name" },
                  { label: this.$t("clients.ceo"), value: "ceo" },
                  { label: this.$t("custom.phone"), value: "entity_phone" },
                  { label: "Email", value: "email" },
                  {
                    label: this.$t("clients.category"),
                    value: (row) => {
                      return this.localeClientCategory(row.category);
                    },
                  },
                  { label: this.$t("clients.tax_number"), value: "inn" },
                  { label: this.$t("clients.debtor"), value: "debtor" },
                  {
                    label: this.$t("clients.branch"),
                    value: (row) => {
                      return this.branchCity(row.company_id);
                    },
                  },
                  { label: this.$t("clients.country"), value: "country" },
                  { label: this.$t("clients.city"), value: "city" },
                  { label: this.$t("clients.address"), value: "entity_adress" },
                  { label: this.$t("clients.tin"), value: "ogrn" },
                  { label: this.$t("clients.bank"), value: "bank" },
                  {
                    label: this.$t("clients.checking_account"),
                    value: "acc_number",
                  },
                  {
                    label: this.$t("clients.correspondent_account"),
                    value: "korr",
                  },
                ],
                content: entity_clients,
              },
            ];
            let settings = {
              fileName: "clients", // Name of the resulting spreadsheet
              extraLength: 3, // A bigger number means that columns will be wider
              writeMode: "write", // The available parameters are 'WriteFile' and 'write'. This setting is optional. Useful in such cases https://docs.sheetjs.com/docs/solutions/output#example-remote-file
              writeOptions: {
                bookType: "xlsx",
                type: "array",
              }, // Style options from https://docs.sheetjs.com/docs/api/write-options
              RTL: false, // Display the columns from right-to-left (the default value is false)
            };
            let wbout = xlsx(data, settings);
            var blob_data = new Blob([new Uint8Array(wbout)], { type: "application/octet-stream" });
            if (!this.isNative) {
              saveAs(blob_data, 'clients.xlsx');
            } else {
              if (Filesystem.checkPermissions()) {
                const getBase64FromBlob = async (blob) => {
                  return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                      const base64data = reader.result;
                      resolve(base64data);
                    }
                  });
                }
                getBase64FromBlob(blob_data).then(
                  (base64) => {
                    // save file
                    async function writeFile() {
                      return await Filesystem.writeFile({
                        path: 'clients.xlsx',
                        data: base64,
                        directory: Directory.Documents,
                      });
                    }
                    let file_uri = null
                    let writedFile = writeFile().then((getUriResult) => {
                      // share file opened file
                      let shareFile = async () => {
                        await Share.share({
                          title: 'Download document',
                          files: [getUriResult.uri],
                        }).catch((error) => {
                          console.log("shareFile", error)
                        });
                      };
                      let sharedFile = shareFile();
                    }).catch((error) => {
                      console.log("shareFile", error)
                    });
                  }
                );
              } else {
                Filesystem.requestPermissions();
              }
            }
          })
          .catch((error) => {
            this.setError(error, this.$t("errors.clients_load"));
            console.log(error);
          })
          .finally(() => (this.isLoading = false));
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    birthdayFormat(date) {
      // FT-143: birthday — строка в исторических форматах (как в birthday_tool.rb бэка);
      // прежний парс только YYYY-MM-DD рисовал из «27.08.1985» мусор вида 19-08-2027.
      // strict-режим (3-й аргумент) — иначе moment угадывает и снова выдумывает даты.
      const parsed = moment(date || "", ["YYYY-MM-DD", "DD.MM.YYYY", "DD/MM/YYYY", "DD-MM-YYYY"], true);
      return parsed.isValid() ? parsed.format("DD-MM-YYYY") : "";
    },
    branchCity(id) {
      let branch = this.branches.find((branch) => {
        return branch.id == id;
      });
      if (branch && branch.city) {
        return branch.city;
      } else {
        return id;
      }
    },
    branchName(id) {
      let branch = this.branches.find((branch) => {
        return branch.id == id;
      });
      if (branch && branch.name) {
        return `ID ${branch.id} | ${branch.name} | ${branch.city}`;
      } else {
        return id;
      }
    },
    replaceToClient(client) {
      this.$router.push({ path: `/clients/${client.id}` });
    },
    async getFullBalance(client, showModal = true) {
      if (!client.yandex_driver_id) {
        this.$notify('Ошибка', 'У водителя нет Yandex.Taxi ID', 'error', 5000);
        return;
      }

      this.isLoadingGetFullBalance = true;
      this.currentDriver = client;
      this.axios.get(
        `/api/v1/taxi/drivers/${client.id}/balance`,
        {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        }
      ).then((response) => {
        if (response.data.success && showModal) {
          const balanceData = response.data.data.balance;
          this.showBalanceModal(client, balanceData);
        } else if (response.data.success && !showModal) {
          this.currentDriver = client;
          this.balanceData = response.data.data.balance;
          this.balanceDialog = true;
          client.balance = response.data.data.balance.balance;
          this.clients.splice(this.clients.indexOf(client), 1, client);
        } else {
          this.$notify('Ошибка', response.data.error || 'Ошибка получения баланса', 'error', 5000);
        }
      }).catch((error) => {
        console.error('Error fetching driver balance:', error);
        this.$notify('Ошибка', 'Ошибка получения баланса водителя', 'error', 5000);
      }).finally(() => (this.isLoadingGetFullBalance = false));
    },
    showBalanceModal(client, balanceData) {
      this.currentDriver = client;
      this.balanceData = balanceData;
      this.balanceDialog = true;
    },
    openTransactionDialog(client, type) {
      this.currentDriver = client;
      this.transactionType = type;
      this.count = {
        sum: '',
        description: '',
      };
      this.transactionDialog = true;
    },
    async createTransaction() {
      if (!this.count.sum || !this.count.description) {
        this.$notify('Ошибка', 'Заполните все поля', 'error', 5000);
        return;
      }

      const amount = parseFloat(this.count.sum);
      if (isNaN(amount) || amount <= 0) {
        this.$notify('Ошибка', 'Введите корректную сумму', 'error', 5000);
        return;
      }

      // Определяем знак суммы
      const signedAmount = this.transactionType === 'add' ? amount.toString() : `-${amount.toString()}`;

      this.isLoadingTransaction = true;
      try {
        const response = await this.axios.post(
          `/api/v1/taxi/drivers/${this.currentDriver.id}/transaction`,
          {
            yandex_integration: {
              amount: signedAmount,
              description: this.count.description,
              kind: 'other',
              cash: this.count.cash,
              cashless: this.count.cashless,
              cash_card: this.count.cash_card,
              entity: this.count.entity,
              operation: parseFloat(signedAmount) > 0 ? true : false,
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        );

        if (response.data.success) {
          this.$notify(
            'Успех',
            `Транзакция на ${signedAmount} ₽ создана успешно. Баланс будет обновлен автоматически через 30 секунд.`,
            'success',
            7000
          );
          this.transactionDialog = false;

          // Обновляем баланс водителя через 35 секунд (с небольшим буфером)
          setTimeout(() => {
            this.getCurrentDriverBalance(this.currentDriver);
          }, 35000);
        } else {
          this.$notify('Ошибка', response.data.error || 'Ошибка создания транзакции', 'error', 5000);
        }
      } catch (error) {
        // Оператор сам отказался отправлять необычно крупную сумму (moneyConfirmGuard):
        // запрос отменён ДО отправки, сбоя не было. Этот catch показывает ошибку МИМО
        // setErrorMixin, поэтому проверка нужна здесь (ревью 29.08.2026, Codex P1).
        if (isMoneyDeclined(error)) return;
        console.error('Error creating transaction:', error);
        this.$notify('Ошибка', 'Ошибка создания транзакции', 'error', 5000);
      } finally {
        this.isLoadingTransaction = false;
      }
    },
    getCurrentDriverBalance() {
      this.getFullBalance(this.currentDriver, false);
    },
    syncTaxiDrivers() {
      this.isLoadingSyncTaxiDrivers = true;
      this.axios.post('/api/v1/taxi/sync',
        {
          company_id: this.$store.getters.getCompany.company.id,
          sync_type: 'drivers'
        },
        {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        }
      ).then((response) => {
        console.log(response);
      }).catch((error) => {
        this.setError(error, this.$t("errors.error"));
      }).finally(() => (this.isLoadingSyncTaxiDrivers = false));
    },
    checkMobile() {
      if (this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm") {
        this.isMobile = true;
        return true;
      } else {
        this.isMobile = false;
        return false;
      }
    },
  },
  components: {
    ClientsCommandBar,
    ClientDuplicatesDialog,
  },
};
</script>
